import type { WebInstrumentTaskIntervalType } from '@/shared/types/web'
import { humanReadableStructuralRrule } from './rrule-utils'

const TIME_UNIT_TO_MINUTES: Record<string, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
  weeks: 10080,
  months: 43200,
  years: 525600,
}

/**
 * Minutes → display unit, matching legacy `convertTimeValue` (time.utils.ts) exactly:
 * integers stay integers (no trailing `.0`), fractions round to at most 2 decimals via
 * `Number(x.toFixed(2))`. Used by both the read-only Frequency Details panel and the
 * interval-option dropdown labels, so a migrated non-clean-multiple value (e.g. `100 min`
 * in hours → `1.67`) renders identically to legacy rather than to full float precision.
 */
export function convertFromMinutes(minutes: number, unit: string): number {
  const rate = TIME_UNIT_TO_MINUTES[unit] ?? 1
  const result = minutes / rate
  return Number.isInteger(result) ? result : Number(result.toFixed(2))
}

/**
 * Mirrors legacy `createIntervalName` — enriches a raw `intervalTypeDescription` with
 * frequency-type prefix, start time (calendar), and lead/overdue suffixes (timed),
 * so similarly-named intervals are distinguishable in any selector.
 *
 * @param interval   The interval type record.
 * @param allOptions The full list of interval types in the current selector; used for
 *                   collision detection to append `#id` when labels still clash.
 */
export function formatIntervalOptionLabel(
  interval: WebInstrumentTaskIntervalType,
  allOptions: WebInstrumentTaskIntervalType[],
): string {
  const code = interval.frequencyTypeCode ?? ''
  const description = interval.intervalTypeDescription ?? String(interval.id)

  let label = description

  if (code === 'CALENDAR_FIXED') {
    // Overdue suffix for calendar (v2 behaviour — always show when present). Starts At is
    // never appended here — it is a per-task value (task_frequencies.fixed_schedule_start_time),
    // not an attribute of the shared interval type, matching legacy's createIntervalName.
    if (interval.lateTimeIntervalHours != null && interval.lateTimeIntervalHours > 0) {
      const unit = interval.lateTimeUnit ?? 'hours'
      const value = convertFromMinutes(interval.lateTimeIntervalHours, unit)
      label += `, Overdue ${value} ${unit}`
    }
  } else if (code === 'TIMED') {
    if (interval.leadTimeIntervalHours != null && interval.leadTimeIntervalHours > 0) {
      const unit = interval.leadTimeUnit ?? 'hours'
      const value = convertFromMinutes(interval.leadTimeIntervalHours, unit)
      label += `, Lead ${value} ${unit}`
    }
    if (interval.lateTimeIntervalHours != null && interval.lateTimeIntervalHours > 0) {
      const unit = interval.lateTimeUnit ?? 'hours'
      const value = convertFromMinutes(interval.lateTimeIntervalHours, unit)
      label += `, Overdue ${value} ${unit}`
    }
  }

  // Append #id when another option in the same list would have the same label
  const collides = allOptions.some(
    (other) => other.id !== interval.id && formatIntervalOptionLabelBase(other) === label,
  )
  if (collides) {
    label += ` #${interval.id}`
  }

  return label
}

/**
 * Builds the label without collision-suffix — used internally by formatIntervalOptionLabel
 * for the collision check without infinite recursion.
 */
function formatIntervalOptionLabelBase(interval: WebInstrumentTaskIntervalType): string {
  const code = interval.frequencyTypeCode ?? ''
  const description = interval.intervalTypeDescription ?? String(interval.id)
  let label = description

  if (code === 'CALENDAR_FIXED') {
    if (interval.lateTimeIntervalHours != null && interval.lateTimeIntervalHours > 0) {
      const unit = interval.lateTimeUnit ?? 'hours'
      const value = convertFromMinutes(interval.lateTimeIntervalHours, unit)
      label += `, Overdue ${value} ${unit}`
    }
  } else if (code === 'TIMED') {
    if (interval.leadTimeIntervalHours != null && interval.leadTimeIntervalHours > 0) {
      const unit = interval.leadTimeUnit ?? 'hours'
      const value = convertFromMinutes(interval.leadTimeIntervalHours, unit)
      label += `, Lead ${value} ${unit}`
    }
    if (interval.lateTimeIntervalHours != null && interval.lateTimeIntervalHours > 0) {
      const unit = interval.lateTimeUnit ?? 'hours'
      const value = convertFromMinutes(interval.lateTimeIntervalHours, unit)
      label += `, Overdue ${value} ${unit}`
    }
  }

  return label
}

/**
 * Builds a human-readable auto-name for a new calendar-fixed frequency definition,
 * mirroring the MaintenanceTaskModal's buildAutoName but usable on Schedule page too.
 * Legacy parity: stays purely structural — Starts At (a per-task value) and Overdue
 * (a live field on the interval type) are never baked in here. Both are added at
 * display time by formatIntervalOptionLabel, so editing either later never leaves this
 * stored description stale (see createIntervalName in frequencies-list.component.ts).
 */
export function buildCalendarAutoName(opts: { frequencyRrule: string }): string {
  return opts.frequencyRrule ? humanReadableStructuralRrule(opts.frequencyRrule) : ''
}

/**
 * Builds a human-readable auto-name for a new timed frequency definition. Legacy parity:
 * stays purely structural — Lead and Overdue are live fields on the interval type, added
 * at display time by formatIntervalOptionLabel rather than baked in here (see
 * buildCalendarAutoName above for why).
 */
export function buildTimedAutoName(opts: {
  repeatsEveryValue: string
  repeatsEveryUnit: string
}): string {
  return opts.repeatsEveryValue !== ''
    ? `Repeats Every ${opts.repeatsEveryValue} ${opts.repeatsEveryUnit}`
    : ''
}
